// ── Component type identifiers ──
export enum ComponentType {
  STRUCTURE = 'structure',
  ARMOR = 'armor',
  BATTERY = 'battery',
  WHEEL = 'wheel',
  THRUSTER = 'thruster',
  SENSOR_SHORT = 'sensorShort',
  SENSOR_MEDIUM = 'sensorMedium',
  CANNON_LIGHT = 'cannonLight',
  CANNON_HEAVY = 'cannonHeavy',
  EXPLOSIVE_CHARGE = 'explosiveCharge',
  BURST_CANNON = 'burstCannon',
  REPAIR_MODULE = 'repairModule',
  RECHARGE_MODULE = 'rechargeModule',
  WING = 'wing',
  MISSILE_SAM = 'missileSam',

  MISSILE_SOL_SOL = 'missileSolSol',
  DIRECTION = 'direction',
  STRUCTURE_TRIANGLE = 'structureTriangle',
  FLAMETHROWER = 'flamethrower',
  LASER = 'laser',
  STAB_LEGS = 'stabLegs',
  TURBINE = 'turbine',
  MINE_LAYER = 'mineLayer',
  ROCKET_POD = 'rocketPod',
  JUMPER = 'jumper',
  EXCAVATOR = 'excavator',
  CAMOUFLAGE = 'camouflage',
  MUD_LAUNCHER = 'mudLauncher',
}

// ── Category for active/passive distinction ──
export enum ComponentCategory {
  PASSIVE = 'passive',
  MOVEMENT = 'movement',
  SENSOR = 'sensor',
  WEAPON = 'weapon',
  SUPPORT = 'support',
}

// ── Weapon targeting category ──
export enum WeaponTarget {
  GROUND = 'ground',         // Can only hit ground enemies
  AIR = 'air',               // Can only hit flying enemies
  BOTH = 'both',             // Can hit both
}

// ── Projectile trajectory type ──
export enum ProjectileType {
  BULLET = 'bullet',         // Straight line, fast
  SHELL = 'shell',           // Slight arc, medium speed
  MISSILE_ARC = 'missileArc', // Arcing missile (SAM / Sol-Sol)
  FLAME = 'flame',           // Short-range cone, continuous
  BEAM = 'beam',             // Instant hit laser beam
  MUD_BLOB = 'mudBlob',      // Arcing mud projectile, converts tiles to MUD
}

// ── Shape for placeholder rendering ──
export enum ComponentShape {
  SQUARE = 'square',
  RECT = 'rect',
  CIRCLE = 'circle',
}

// ── Single component definition ──
export interface ComponentDef {
  type: ComponentType;
  name: string;
  category: ComponentCategory;
  shape: ComponentShape;
  color: number;
  cost: number;
  // Hand-tuned assembly cost per evolution level [lvl1, lvl2, lvl3]
  costPerLevel: [number, number, number];
  mass: number;
  hp: number;
  // Energy: positive = provides, negative = consumes per second
  energy: number;
  // Movement force (only for wheels/thrusters)
  force: number;
  // Sensor range in pixels (only for sensors)
  sensorRange: number;
  // Weapon stats
  damage: number;
  fireRate: number;      // shots per second
  weaponRange: number;   // pixels
  // Support stats
  repairRate: number;    // hp per second
  rechargeRate: number;  // energy per second
  supportRange: number;  // pixels
  // Explosive
  explosionDamage: number;
  explosionRadius: number;
  // Visual size (width, height in pixels for rendering)
  width: number;
  height: number;
  // Weapon targeting restriction
  weaponTarget: WeaponTarget;
  // Projectile type for trajectory
  projectileType: ProjectileType;
  // Projectile speed in px/s (0 = instant)
  projectileSpeed: number;
  // Whether this weapon uses shared vision (can target enemies seen by allies)
  sharedVision: boolean;
  // Number of projectiles per salvo (e.g. rocket pod fires multiple missiles per shot)
  salvoCount: number;
  // Short description for tooltip
  description: string;
  // Edge connectivity: which edges allow connections [top, right, bottom, left]
  // Rotation shifts these values (90° clockwise: [top,right,bottom,left] → [left,top,right,bottom])
  allowedEdges: [boolean, boolean, boolean, boolean];
  // Forbidden edges: neighbor cell MUST be empty [top, right, bottom, left]
  // Used for clearance (barrel, exhaust, nozzle). Implies allowedEdges[i]=false.
  forbiddenEdges: [boolean, boolean, boolean, boolean];
  // Visual overrides (editable via WorkshopGOD scene)
  offsetX: number;          // pixel offset within grid cell (default 0)
  offsetY: number;          // pixel offset within grid cell (default 0)
  defaultRotation: number;  // radians: 0, π/2, π, 3π/2 (default 0)
  scaleX: number;           // horizontal scale factor (default 1)
  scaleY: number;           // vertical scale factor (default 1)
  flipX: boolean;           // mirror horizontally (default false)
  flipY: boolean;           // mirror vertically (default false)
  // Grid click mode: 'rotate' = 90° rotation per click, 'flip' = cycle flipX → flipY → normal
  gridClickMode: 'rotate' | 'flip';
  // Ability cooldown in seconds (jumper push, mine lay, excavator harvest). 0 = no cooldown
  cooldown: number;
  // Armor shield: blue protection layer per module (0 = no shield)
  shieldHp: number;
  // Ricochet chance for non-explosive projectiles (0.0 - 1.0)
  ricochetChance: number;
  // Ricochet chance for beam/laser projectiles (0.0 - 1.0)
  ricochetBeamChance: number;
  // Campaign unlock cost (0 = free / starting component)
  unlockCost: number;
  // Evolution system: tier of this component in its evolution chain (0 = base)
  evolutionTier: number;
  // Max evolution level (0 = no evolution available)
  maxEvolutionLevel: number;
  // Extensible unlock conditions (optional — falls back to unlockCost when absent)
  unlockRequirements?: UnlockRequirement;
}

/** Extensible unlock condition for components.
 *  When absent on a ComponentDef, the system falls back to { currency: unlockCost }. */
export interface UnlockRequirement {
  /** Minimum currency needed */
  currency?: number;
  // Future fields — not implemented yet, reserved for extensibility:
  // resources?: Partial<Record<string, number>>;
  // storyFlag?: string;
  // eventTriggered?: string;
  // minKills?: Record<string, number>;
}

// ── Helper to create a component def with defaults ──
function def(partial: Partial<ComponentDef> & Pick<ComponentDef, 'type' | 'name' | 'category' | 'shape' | 'color' | 'cost' | 'mass' | 'hp' | 'width' | 'height'>): ComponentDef {
  return {
    energy: 0,
    force: 0,
    sensorRange: 0,
    damage: 0,
    fireRate: 0,
    weaponRange: 0,
    repairRate: 0,
    rechargeRate: 0,
    supportRange: 0,
    explosionDamage: 0,
    explosionRadius: 0,
    weaponTarget: WeaponTarget.GROUND,
    projectileType: ProjectileType.BULLET,
    projectileSpeed: 0,
    sharedVision: false,
    salvoCount: 0,
    description: '',
    allowedEdges: [true, true, true, true],  // default: all edges connectable
    forbiddenEdges: [false, false, false, false],  // default: no forbidden edges
    offsetX: 0,
    offsetY: 0,
    defaultRotation: 0,
    scaleX: 1,
    scaleY: 1,
    flipX: false,
    flipY: false,
    gridClickMode: 'rotate' as const,
    cooldown: 0,
    shieldHp: 0,
    ricochetChance: 0,
    ricochetBeamChance: 0,
    unlockCost: 0,
    costPerLevel: [0, 0, 0],
    evolutionTier: 0,
    maxEvolutionLevel: 0,
    ...partial,
  };
}

// ── All 12 component definitions ──
export const COMPONENT_DEFS: Record<ComponentType, ComponentDef> = {
  [ComponentType.STRUCTURE]: def({
    type: ComponentType.STRUCTURE,
    name: 'Structure',
    category: ComponentCategory.PASSIVE,
    shape: ComponentShape.SQUARE,
    color: 0x888888,
    cost: 5,
    costPerLevel: [5, 8, 12],
    mass: 10,
    hp: 50,
    width: 28,
    height: 28,
    description: 'Basic chassis, adds HP. Light, ideal as foundation.',
    unlockCost: 0, // starting component
    maxEvolutionLevel: 3,
  }),

  [ComponentType.ARMOR]: def({
    type: ComponentType.ARMOR,
    name: 'Armor',
    category: ComponentCategory.PASSIVE,
    shape: ComponentShape.SQUARE,
    color: 0x224466,
    cost: 25,
    costPerLevel: [25, 35, 50],
    mass: 30,
    hp: 150,
    width: 28,
    height: 28,
    description: 'Very tough but heavy — slows the robot. Blue shield absorbs damage first. Higher tiers ricochet projectiles.',
    shieldHp: 40,
    unlockCost: 280,
    maxEvolutionLevel: 3,
  }),

  [ComponentType.BATTERY]: def({
    type: ComponentType.BATTERY,
    name: 'Battery',
    category: ComponentCategory.PASSIVE,
    shape: ComponentShape.RECT,
    color: 0xccaa00,
    cost: 10,
    costPerLevel: [10, 16, 22],
    mass: 8,
    hp: 30,
    energy: 70,
    width: 32,
    height: 20,
    description: 'Essential: without energy, weapons and sensors won\'t work.',
    unlockCost: 0, // starting component
    maxEvolutionLevel: 3,
    allowedEdges: [false, true, true, true], // top blocked (terminals), right/bottom/left open
  }),

  [ComponentType.WHEEL]: def({
    type: ComponentType.WHEEL,
    name: 'Wheel',
    category: ComponentCategory.MOVEMENT,
    shape: ComponentShape.CIRCLE,
    color: 0x222222,
    cost: 15,
    costPerLevel: [15, 22, 30],
    mass: 5,
    hp: 20,
    energy: -3,
    force: 40,     // 1 tile/s at reference mass (40px = 1 tile)
    width: 16,
    height: 16,
    description: 'Adds mobility. More wheels = more speed, especially on heavy robots.',
    unlockCost: 50,
    maxEvolutionLevel: 2,
  }),

  [ComponentType.THRUSTER]: def({
    type: ComponentType.THRUSTER,
    name: 'Thruster',
    category: ComponentCategory.MOVEMENT,
    shape: ComponentShape.RECT,
    color: 0xdd6600,
    cost: 35,
    costPerLevel: [35, 48, 65],
    mass: 8,
    hp: 15,
    energy: -10,
    force: 80,     // 2 tiles/s at reference mass
    width: 24,
    height: 14,
    description: '2x faster than a wheel, but drains a lot of energy. Plan extra batteries.',
    allowedEdges: [true, false, true, true],  // base(left) + flanks, exhaust(right) blocked
    forbiddenEdges: [false, true, false, false],  // exhaust side must be clear
    unlockCost: 250,
    maxEvolutionLevel: 2,
  }),

  [ComponentType.SENSOR_SHORT]: def({
    type: ComponentType.SENSOR_SHORT,
    name: 'Short Sensor',
    category: ComponentCategory.SENSOR,
    shape: ComponentShape.CIRCLE,
    color: 0x44cc44,
    cost: 12,
    costPerLevel: [12, 18, 26],
    mass: 3,
    hp: 10,
    energy: -1,
    sensorRange: 80,
    width: 12,
    height: 12,
    description: 'Short range (80px). Without a sensor, the robot can\'t see or shoot.',
    unlockCost: 0, // starter component
    maxEvolutionLevel: 3,
  }),

  [ComponentType.SENSOR_MEDIUM]: def({
    type: ComponentType.SENSOR_MEDIUM,
    name: 'Medium Sensor',
    category: ComponentCategory.SENSOR,
    shape: ComponentShape.CIRCLE,
    color: 0x228822,
    cost: 45,
    costPerLevel: [45, 62, 82],
    mass: 5,
    hp: 15,
    energy: -2,
    sensorRange: 160,
    width: 14,
    height: 14,
    description: 'Medium range (160px). Lets you fire before the enemy gets too close.',
    unlockCost: 500,
    maxEvolutionLevel: 3,
  }),

  [ComponentType.CANNON_LIGHT]: def({
    type: ComponentType.CANNON_LIGHT,
    name: 'Light Cannon',
    category: ComponentCategory.WEAPON,
    shape: ComponentShape.RECT,
    color: 0xcc2222,
    cost: 35,
    costPerLevel: [35, 45, 58],
    mass: 10,
    hp: 25,
    energy: -5,
    damage: 10,
    fireRate: 1.2,
    weaponRange: 140,
    weaponTarget: WeaponTarget.GROUND,
    projectileType: ProjectileType.BULLET,
    projectileSpeed: 350,
    width: 28,
    height: 10,
    description: 'Ground only. Good damage/cost ratio. Limited range. Low energy use.',
    allowedEdges: [true, false, true, true],  // base(left) + flanks, no barrel(right)
    forbiddenEdges: [false, true, false, false],  // barrel must be clear
    unlockCost: 0, // starting component
    maxEvolutionLevel: 3,
  }),

  [ComponentType.CANNON_HEAVY]: def({
    type: ComponentType.CANNON_HEAVY,
    name: 'Heavy Cannon',
    category: ComponentCategory.WEAPON,
    shape: ComponentShape.RECT,
    color: 0x881111,
    cost: 65,
    costPerLevel: [65, 85, 110],
    mass: 25,
    hp: 40,
    energy: -10,
    damage: 35,
    fireRate: 0.4,
    weaponRange: 180,
    weaponTarget: WeaponTarget.GROUND,
    projectileType: ProjectileType.SHELL,
    projectileSpeed: 250,
    width: 34,
    height: 14,
    description: 'Ground only. Devastating but slow and heavy. Arc shells with AoE splash. Ideal on fixed turrets.',
    allowedEdges: [true, false, true, true],  // base(left) + flanks, no barrel(right)
    forbiddenEdges: [false, true, false, false],  // barrel must be clear
    unlockCost: 400,
    maxEvolutionLevel: 3,
  }),

  [ComponentType.EXPLOSIVE_CHARGE]: def({
    type: ComponentType.EXPLOSIVE_CHARGE,
    name: 'Explosive Charge',
    category: ComponentCategory.WEAPON,
    shape: ComponentShape.SQUARE,
    color: 0xff4400,
    cost: 35,
    costPerLevel: [35, 50, 70],
    mass: 12,
    hp: 10,
    explosionDamage: 120,
    explosionRadius: 60,
    width: 22,
    height: 22,
    description: 'Destroys the robot! Maximize speed to charge into enemy groups.',
    unlockCost: 120,
    maxEvolutionLevel: 3,
  }),

  [ComponentType.BURST_CANNON]: def({
    type: ComponentType.BURST_CANNON,
    name: 'Burst Cannon',
    category: ComponentCategory.WEAPON,
    shape: ComponentShape.RECT,
    color: 0xee5522,
    cost: 45,
    costPerLevel: [45, 60, 78],
    mass: 15,
    hp: 30,
    energy: -7,
    damage: 6,         // 6 per shot × 3 burst = 18 total
    fireRate: 2.5,     // 2.5 shots/s internal (fires 3 rapid shots then pauses)
    weaponRange: 120,
    weaponTarget: WeaponTarget.BOTH,
    projectileType: ProjectileType.BULLET,
    projectileSpeed: 400,
    width: 30,
    height: 12,
    description: 'Ground+Air. 3-round burst then pause. Hits both. Good close-range DPS, overheats.',
    allowedEdges: [true, false, true, true],  // base(left) + flanks, no barrel(right)
    forbiddenEdges: [false, true, false, false],  // barrel must be clear
    unlockCost: 200,
    maxEvolutionLevel: 3,
  }),

  [ComponentType.REPAIR_MODULE]: def({
    type: ComponentType.REPAIR_MODULE,
    name: 'Repair Module',
    category: ComponentCategory.SUPPORT,
    shape: ComponentShape.RECT,
    color: 0x44cccc,
    cost: 30,
    costPerLevel: [30, 42, 56],
    mass: 8,
    hp: 20,
    energy: -3,
    repairRate: 10,
    supportRange: 57,  // 1 diagonal (TILE_SIZE × √2) — evolution adds +50%/level
    width: 24,
    height: 18,
    description: 'Heals nearby allies & base. Range: 1 diag. Upgrades: +rate, +50% range, −energy.',
    unlockCost: 220,
    maxEvolutionLevel: 3,
  }),

  [ComponentType.RECHARGE_MODULE]: def({
    type: ComponentType.RECHARGE_MODULE,
    name: 'Recharge Module',
    category: ComponentCategory.SUPPORT,
    shape: ComponentShape.RECT,
    color: 0x8844cc,
    cost: 30,
    costPerLevel: [30, 42, 56],
    mass: 8,
    hp: 20,
    energy: -4,
    rechargeRate: 10,
    supportRange: 57,  // 1 diagonal (TILE_SIZE × √2) — evolution adds +50%/level
    width: 24,
    height: 18,
    description: 'Recharges ally energy. Range: 1 diag. Upgrades: +rate, +50% range, −energy.',
    unlockCost: 220,
    maxEvolutionLevel: 3,
  }),

  [ComponentType.WING]: def({
    type: ComponentType.WING,
    name: 'Wings',
    category: ComponentCategory.MOVEMENT,
    shape: ComponentShape.RECT,
    color: 0x6699dd,
    cost: 60,
    costPerLevel: [60, 80, 105],
    mass: 6,
    hp: 15,
    energy: -6,
    force: 80,     // 2× wheel force = 2 tiles/s at reference mass
    width: 30,
    height: 10,
    description: 'Free flight: ignores obstacles, moves diagonally. 2x faster than a wheel.',
    allowedEdges: [false, true, false, false],  // right edge only (attaches to fuselage on the right side)
    unlockCost: 600,
    maxEvolutionLevel: 2,
  }),
  [ComponentType.MISSILE_SAM]: def({
    type: ComponentType.MISSILE_SAM,
    name: 'AA Missile',
    category: ComponentCategory.WEAPON,
    shape: ComponentShape.RECT,
    color: 0x44aaff,
    cost: 55,
    costPerLevel: [55, 72, 92],
    mass: 14,
    hp: 20,
    energy: -4,
    damage: 25,
    fireRate: 0.2,
    weaponRange: 200,
    weaponTarget: WeaponTarget.BOTH,
    projectileType: ProjectileType.MISSILE_ARC,
    projectileSpeed: 220,
    width: 26,
    height: 12,
    description: 'Ground+Air. Arc-trajectory missile hitting both ground and air targets. ~20/shot.',
    allowedEdges: [true, false, true, true],  // base(left) + flanks, no tip(right)
    forbiddenEdges: [false, true, false, false],  // tip must be clear
    unlockCost: 350,
    maxEvolutionLevel: 3,
  }),

  [ComponentType.MISSILE_SOL_SOL]: def({
    type: ComponentType.MISSILE_SOL_SOL,
    name: 'Ground Missile',
    category: ComponentCategory.WEAPON,
    shape: ComponentShape.RECT,
    color: 0xcc6622,
    cost: 80,
    costPerLevel: [80, 105, 135],
    mass: 18,
    hp: 25,
    energy: -6,
    damage: 35,
    fireRate: 0.18,
    weaponRange: 300,
    weaponTarget: WeaponTarget.GROUND,
    projectileType: ProjectileType.MISSILE_ARC,
    projectileSpeed: 180,
    sharedVision: true,
    width: 30,
    height: 14,
    description: 'Ground only. Long range. Can fire at enemies seen by allies. Arc trajectory. ~33/shot.',
    allowedEdges: [true, false, true, true],  // base(left) + flanks, no tip(right)
    forbiddenEdges: [false, true, false, false],  // tip must be clear
    unlockCost: 900,
    maxEvolutionLevel: 3,
  }),

  [ComponentType.DIRECTION]: def({
    type: ComponentType.DIRECTION,
    name: 'Direction',
    category: ComponentCategory.PASSIVE,
    shape: ComponentShape.SQUARE,
    color: 0x88bbff,
    cost: 0,
    costPerLevel: [0, 0, 0],
    mass: 0,
    hp: 0,
    width: 20,
    height: 20,
    description: 'Focuses sensors into a cone: +50% range, +20% accuracy. Evolves to widen cone and boost further.',
    unlockCost: 0, // free utility component
    maxEvolutionLevel: 0, // direction has no upgrades
  }),

  [ComponentType.STRUCTURE_TRIANGLE]: def({
    type: ComponentType.STRUCTURE_TRIANGLE,
    name: 'Bracket',
    category: ComponentCategory.PASSIVE,
    shape: ComponentShape.SQUARE,
    color: 0x999999,
    cost: 8,
    costPerLevel: [8, 12, 16],
    mass: 6,
    hp: 45,
    width: 28,
    height: 28,
    description: 'Right-angle bracket. Light, cheap. Only connects on 2 edges (the legs).',
    allowedEdges: [false, false, true, true],  // right angle at bottom-left: bottom + left connectable
    unlockCost: 30,
    maxEvolutionLevel: 2, // 3 tiers: Bracket_0, Bracket_1, Bracket_2
  }),

  [ComponentType.FLAMETHROWER]: def({
    type: ComponentType.FLAMETHROWER,
    name: 'Flamethrower',
    category: ComponentCategory.WEAPON,
    shape: ComponentShape.RECT,
    color: 0xff6600,
    cost: 40,
    costPerLevel: [40, 55, 72],
    mass: 14,
    hp: 25,
    energy: -6,
    damage: 6,           // DPS per tick (continuous), effective ~12 DPS at fireRate 2
    fireRate: 2,         // ticks per second
    weaponRange: 80,     // very short range
    weaponTarget: WeaponTarget.GROUND,
    projectileType: ProjectileType.FLAME,
    projectileSpeed: 150,
    width: 30,
    height: 14,
    description: 'Ground only. Very short range, continuous fire cone. Melts groups of enemies.',
    allowedEdges: [true, false, true, true],  // base + flanks, no nozzle(right)
    forbiddenEdges: [false, true, false, false],  // nozzle must be clear
    unlockCost: 320,
    maxEvolutionLevel: 3,
  }),

  [ComponentType.LASER]: def({
    type: ComponentType.LASER,
    name: 'High Laser',
    category: ComponentCategory.WEAPON,
    shape: ComponentShape.RECT,
    color: 0xff0044,
    cost: 70,
    costPerLevel: [70, 92, 120],
    mass: 20,
    hp: 30,
    energy: -6,
    damage: 60,          // high damage per shot (was 45)
    fireRate: 0.3,       // slow: 1 shot every 3.3s
    weaponRange: 220,    // long range
    weaponTarget: WeaponTarget.BOTH,
    projectileType: ProjectileType.BEAM,
    projectileSpeed: 0,  // instant hit
    width: 32,
    height: 10,
    description: 'Ground+Air. Instant beam, high damage. Very slow fire rate. ~20/shot.',
    allowedEdges: [true, false, true, true],  // base + flanks, no emitter(right)
    forbiddenEdges: [false, true, false, false],  // emitter must be clear
    unlockCost: 1200,
    maxEvolutionLevel: 3,
  }),

  [ComponentType.STAB_LEGS]: def({
    type: ComponentType.STAB_LEGS,
    name: 'Stab. Legs',
    category: ComponentCategory.PASSIVE,
    shape: ComponentShape.SQUARE,
    color: 0xaa8844,
    cost: 45,
    costPerLevel: [45, 60, 78],
    mass: 15,
    hp: 80,
    width: 24,
    height: 24,
    description: 'Stabilization legs. Fixed platform: 100% stability, +30% accuracy, 20% damage reduction. Limit 1 per robot. Incompatible with wheels/wings.',
    unlockCost: 100,
    maxEvolutionLevel: 2,
  }),

  [ComponentType.TURBINE]: def({
    type: ComponentType.TURBINE,
    name: 'Turbine',
    category: ComponentCategory.MOVEMENT,
    shape: ComponentShape.SQUARE,
    color: 0x55aacc,
    cost: 75,
    costPerLevel: [75, 100, 130],
    mass: 8,
    hp: 20,
    energy: -8,
    force: 70,     // slower than wings (80) but allows hover (was 50/12)
    width: 24,
    height: 24,
    description: 'Helicopter flight: hover in place, ignore obstacles. Slower than wings but stable.',
    unlockCost: 750,
    maxEvolutionLevel: 2,
  }),

  // ── Mine Layer: drops proximity mines with countdown + stun/pushback ──
  [ComponentType.MINE_LAYER]: def({
    type: ComponentType.MINE_LAYER,
    name: 'Mine Layer',
    category: ComponentCategory.SUPPORT,
    shape: ComponentShape.SQUARE,
    color: 0x886622,
    cost: 38,
    costPerLevel: [38, 52, 68],
    mass: 10,
    hp: 20,
    energy: -5,
    damage: 60,           // mine damage
    weaponRange: 30,      // trigger radius (px)
    supportRange: 40,     // laying range (~1 tile around robot)
    explosionDamage: 20,  // AoE splash
    explosionRadius: 40,  // explosion radius (px)
    cooldown: 3.0,        // seconds between mine lays
    width: 24,
    height: 24,
    description: 'Mines à proximité. Peu de dégâts mais stun + pushback. Réactions en chaîne!',
    allowedEdges: [false, true, true, true] as [boolean, boolean, boolean, boolean],
    forbiddenEdges: [true, false, false, false],  // mine ejection port (top) must be clear
    unlockCost: 250,
    maxEvolutionLevel: 3,
  }),

  // ── Rocket Pod: multi-missile salvo weapon, low precision, devastating vs groups ──
  [ComponentType.ROCKET_POD]: def({
    type: ComponentType.ROCKET_POD,
    name: 'Rocket Pod',
    category: ComponentCategory.WEAPON,
    shape: ComponentShape.RECT,
    color: 0xcc4400,
    cost: 100,
    costPerLevel: [100, 130, 168],
    mass: 18,
    hp: 25,
    energy: -5,
    damage: 10,
    fireRate: 0.12,
    weaponRange: 180,
    weaponTarget: WeaponTarget.BOTH,
    projectileType: ProjectileType.MISSILE_ARC,
    projectileSpeed: 180,
    salvoCount: 5,
    explosionDamage: 30,   // AoE splash per missile
    explosionRadius: 45,   // blast radius (px)
    width: 30,
    height: 14,
    description: 'Ground+Air. Fires 5 homing missiles per salvo. Low precision, devastating vs groups. ~42/salvo.',
    allowedEdges: [true, false, true, true],  // base(left) + flanks, no tip(right)
    forbiddenEdges: [false, true, false, false],  // pod must be clear
    unlockCost: 1500,
    maxEvolutionLevel: 3,
  }),

  // ── Jumper: spring-loaded component with dual behavior ──
  // Bottom: robot moves by bouncing (hop locomotion)
  // Sides: pushes enemy/ally in jumper direction on cooldown
  [ComponentType.JUMPER]: def({
    type: ComponentType.JUMPER,
    name: 'Jumper',
    category: ComponentCategory.SUPPORT,
    shape: ComponentShape.SQUARE,
    color: 0x44dd88,
    cost: 40,
    costPerLevel: [40, 55, 72],
    mass: 10,
    hp: 30,
    energy: -4,
    force: 60,             // bounce force (used for hop movement when bottom)
    damage: 0,             // no direct damage
    supportRange: 50,      // push trigger range (px) for side mode
    weaponRange: 40,       // push detection radius
    cooldown: 3.0,         // seconds between pushes (side mode)
    width: 22,
    height: 22,
    description: 'Bottom: bouncing movement. Sides: push enemies/allies in jumper direction. Spring-loaded!',
    allowedEdges: [true, true, true, true],  // can connect on all sides
    unlockCost: 250,
    maxEvolutionLevel: 3,
  }),

  // ── Excavator: passive resource harvester, collects from nearby decorations ──
  [ComponentType.EXCAVATOR]: def({
    type: ComponentType.EXCAVATOR,
    name: 'Excavator',
    category: ComponentCategory.SUPPORT,
    shape: ComponentShape.RECT,
    color: 0xcc8833,
    cost: 20,
    costPerLevel: [20, 28, 38],
    mass: 8,
    hp: 15,
    energy: -2,
    supportRange: 40,     // harvest range (px) — ~1 tile radius
    cooldown: 5.0,        // seconds between harvests
    width: 22,
    height: 18,
    description: 'Récolte les ressources (Co, ◻) des décors à proximité. Passif, automatique.',
    allowedEdges: [true, true, true, true],
    unlockCost: 80,
    maxEvolutionLevel: 3,
  }),

  [ComponentType.CAMOUFLAGE]: def({
    type: ComponentType.CAMOUFLAGE,
    name: 'Camouflage',
    category: ComponentCategory.SUPPORT,
    shape: ComponentShape.SQUARE,
    color: 0x338866,
    cost: 35,
    costPerLevel: [35, 48, 65],
    mass: 5,
    hp: 12,
    energy: -4,
    supportRange: 0,
    width: 22,
    height: 22,
    description: 'Rend le robot invisible aux détecteurs ennemis tant qu\'il a de l\'énergie. Effet hologramme.',
    allowedEdges: [true, true, true, true],
    unlockCost: 280,
    maxEvolutionLevel: 3,
  }),

  // ── Mud Launcher: lobs mud blobs that deal light damage and convert tiles to MUD ──
  [ComponentType.MUD_LAUNCHER]: def({
    type: ComponentType.MUD_LAUNCHER,
    name: 'Mud Launcher',
    category: ComponentCategory.WEAPON,
    shape: ComponentShape.RECT,
    color: 0x886633,
    cost: 45,
    costPerLevel: [45, 45, 45],
    mass: 14,
    hp: 20,
    energy: -6,
    damage: 5,              // low damage — terrain control is the purpose
    fireRate: 0.5,           // 1 shot every 2 seconds
    weaponRange: 160,
    weaponTarget: WeaponTarget.GROUND,
    projectileType: ProjectileType.MUD_BLOB,
    projectileSpeed: 200,
    explosionRadius: 30,     // mud splash radius (px)
    width: 28,
    height: 14,
    description: 'Sol uniquement. Peu de dégâts mais transforme le terrain en boue, ralentissant les ennemis.',
    allowedEdges: [true, false, true, true] as [boolean, boolean, boolean, boolean],
    forbiddenEdges: [false, true, false, false],  // nozzle must be clear
    unlockCost: 200,
    maxEvolutionLevel: 0,
  }),
};

// ── Excavator configuration constants ──
export const EXCAVATOR_CONFIG = {
  harvestCooldown: 5.0,    // seconds between harvests (base)
  energyPerHarvest: 3,     // energy cost per harvest
  yieldMultBase: 1.0,      // base yield multiplier
};

// ── Mine Layer configuration constants ──
export const MINE_CONFIG = {
  stunDuration: 3.0,        // seconds (2-5s with evolution)
  pushbackTiles: 2,         // base pushback distance in tiles
  energyPerMine: 8,         // energy cost to lay one mine
  layCooldown: 3.0,         // seconds between mine lays
  maxMinesPerTile: 2,       // base max per tile (+ evolutionLevel)
  countdownSec: 1.5,        // countdown before explosion (2x faster)
  chainDelay: 0.3,          // seconds delay for chain reaction detonations
  maxChainDepth: 10,        // safety cap for chain reactions
};

// ── Convenience: get all component types as array ──
export const ALL_COMPONENT_TYPES = Object.values(ComponentType);

// ══════════════════════════════════════════
//  AUTO-COST FORMULA
//  Computes a component's cost from its stats so balance stays consistent.
//  Each stat contributes a weighted value; total is rounded to nearest integer.
//  Weights calibrated against the hand-tuned costs for sanity.
// ══════════════════════════════════════════

/**
 * Compute cost from stats automatically.
 * Each stat contributes a weighted value. Weights are tuned so that:
 *   - Structural parts (Structure, Armor) cost ~2–12
 *   - Mid-range weapons cost ~8–20
 *   - Top-tier weapons cost ~25–40
 *   - Utility (Battery, Sensor, Wheel) cost ~4–18
 */
export function computeComponentCost(d: ComponentDef): number {
  // Offensive: power-law of DPS and range (diminishing returns on extreme DPS,
  // super-linear on range so long-range weapons cost more)
  const dpsStat = d.damage * d.fireRate;
  const targetMult = d.weaponTarget === WeaponTarget.BOTH ? 1.3 : 1.0;
  const offensive = dpsStat > 0
    ? Math.pow(dpsStat, 0.7) * Math.pow(d.weaponRange / 100, 1.2) * targetMult * 0.7
    : 0;

  // Shared vision (indirect fire = strategic value)
  const visionBonus = d.sharedVision ? offensive * 0.3 : 0;

  // Defensive: HP value
  const defensive = d.hp * 0.15;

  // Energy supply
  const energyVal = d.energy > 0 ? d.energy * 0.2 : 0;

  // Mobility
  const mobility = d.force * 0.2;

  // Sensor
  const sensor = d.sensorRange * 0.08;

  // Support
  const support = (d.repairRate + d.rechargeRate) * (d.supportRange / 40) * 0.8;

  // Explosive
  const explosive = d.explosionDamage * (d.explosionRadius / 60) * 0.06;

  const raw = offensive + visionBonus + defensive + energyVal + mobility + sensor + support + explosive;
  return Math.max(1, Math.round(raw));
}

export const ASSEMBLY_COST_SCALE = 1.0;   // global cost multiplier
export const UNLOCK_COST_SCALE  = 1.0;    // global unlock cost multiplier

// Apply cost scaling to all component defs at load time.
// Assembly costs are now hand-tuned via costPerLevel (auto-cost formula kept for reference only).
(function applyCostCurves() {
  for (const d of Object.values(COMPONENT_DEFS) as ComponentDef[]) {
    // Use hand-tuned base cost (costPerLevel[0]) — no longer auto-computed
    d.cost = d.costPerLevel[0];
    if (d.cost > 0) d.cost = Math.round(d.cost * ASSEMBLY_COST_SCALE);
    if (d.unlockCost > 0) d.unlockCost = Math.round(d.unlockCost * UNLOCK_COST_SCALE);
  }
})();

// ── Evolution system ──
// Each level grants +15% to key stats (compounding)
// Level 0 = base (Lv.1 display), level 3 = max (Lv.4 display = 1.15^3 ≈ +52%)
export const EVOLUTION_STAT_MULTIPLIER = 0.15; // +15% per level

// Fallback costs (used if component has no evolution chain defined)
export const EVOLUTION_COSTS = [300, 650, 1000]; // Lv.1→2, Lv.2→3, Lv.3→4

/** Get stat multiplier for a given evolution level */
export function getEvolutionMultiplier(level: number): number {
  return Math.pow(1 + EVOLUTION_STAT_MULTIPLIER, level);
}

// Late-bound reference to EVOLUTION_MAP (set by ComponentEvolution.ts at import time
// to avoid circular dependency — ComponentDefs cannot import ComponentEvolution directly)
let _evolutionMapRef: Map<ComponentType, { stages: { level: number; cost: number }[] }> | null = null;

/** Called by ComponentEvolution.ts to register the map without circular import */
export function registerEvolutionMap(map: Map<ComponentType, { stages: { level: number; cost: number }[] }>): void {
  _evolutionMapRef = map;
}

/** Get cost to upgrade from current level.
 *  Uses per-component chain costs from ComponentEvolution if available,
 *  otherwise falls back to flat EVOLUTION_COSTS. */
export function getEvolutionCost(currentLevel: number, maxLevel: number, type?: ComponentType): number {
  if (currentLevel >= maxLevel) return Infinity;
  // Try chain-specific cost
  if (type && _evolutionMapRef) {
    const chain = _evolutionMapRef.get(type);
    if (chain) {
      const stage = chain.stages[currentLevel];
      if (stage) return stage.cost;
    }
  }
  return EVOLUTION_COSTS[currentLevel] ?? Infinity;
}

/** Get assembly cost for a component at a given evolution level */
export function getComponentCostAtLevel(type: ComponentType, level: number): number {
  const def = COMPONENT_DEFS[type];
  const clampedLevel = Math.min(level, 2);
  return def.costPerLevel[clampedLevel];
}
